import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { AuthApiService } from '@core/services/api/auth.service';
import { AuthStateService } from '@shared/services/auth-state.service';
import { TokenRefreshService } from '@shared/services/token-refresh.service';
import { catchError, defer, filter, ReplaySubject, retry, switchMap, take, throwError, timer } from 'rxjs';

// Singleton para evitar múltiples refresh simultáneos
let refreshInProgress = false;
const refreshTokenSubject = new ReplaySubject<any>(1);

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const excludedPaths = [
    '/auth/v2/login',
    '/auth/v2/refresh-token',
    '/auth/v2/logout',
    '/auth/v2/reset-password',
  ];
  if (excludedPaths.some((p) => req.url.includes(p))) {
    return next(req);
  }

  const auth = inject(AuthApiService);
  const router = inject(Router);
  const authState = inject(AuthStateService);
  const tokenSync = inject(TokenRefreshService);

  // Verificar si esta petición ya fue reintentada (evitar bucles infinitos)
  const isRetry = req.headers.has('X-Retry-Auth');

  const authReq = req.clone({
    setHeaders: {
      Authorization: `Bearer ${authState.accessToken() ?? ''}`,
    },
    withCredentials: true,
  });

  return next(authReq).pipe(
    catchError((err: HttpErrorResponse) => {
      // Si NO es un error 401, propagar el error sin intentar refresh
      if (err.status !== 401) {
        return throwError(() => err);
      }

      // Si ya fue reintentada una vez Y sigue fallando con 401, desloguear
      // (esto evita bucles infinitos de refresh)
      if (isRetry) {
        authState.setSession(null);
        router.navigate(['/home']);
        return throwError(() => err);
      }

      // Si ya hay un refresh en progreso, esperar a que termine
      if (refreshInProgress) {
        return refreshTokenSubject.pipe(
          filter((result) => result !== null && result !== undefined),
          take(1),
          switchMap((rs) => {
            if (rs && rs.access_token) {
              const retryReq = req.clone({
                setHeaders: {
                  Authorization: `Bearer ${rs.access_token}`,
                  'X-Retry-Auth': 'true',
                },
                withCredentials: true,
              });
              return next(retryReq);
            }
            authState.setSession(null);
            router.navigate(['/home']);
            return throwError(() => err);
          }),
        );
      }

      // Iniciar nuevo refresh con reintentos y backoff
      refreshInProgress = true;

      return defer(() => auth.refresh()).pipe(
        retry({
          count: 2,
          delay: (retryErr, retryCount) => {
            const isNetworkError = retryErr instanceof HttpErrorResponse && retryErr.status === 0;
            if (!isNetworkError) {
              // Si es 401 u otro error del servidor, no reintentar
              return throwError(() => retryErr);
            }
            // Solo reintentar en errores de red con backoff progresivo: 1s, 2s
            return timer(1000 * retryCount);
          },
        }),
        switchMap((rs) => {
          refreshInProgress = false;

          if (rs && rs.access_token) {
            // Actualizar el token en el estado
            authState.setAccessToken(rs.access_token);
            // Sincronizar con otras pestañas vía BroadcastChannel
            tokenSync.broadcastToken(rs.access_token);
            // Notificar a todos los subscribers que esperan el refresh
            refreshTokenSubject.next(rs);

            const retryReq = req.clone({
              setHeaders: {
                Authorization: `Bearer ${rs.access_token}`,
                'X-Retry-Auth': 'true',
              },
              withCredentials: true,
            });
            return next(retryReq);
          } else {
            refreshTokenSubject.next(null);
            authState.setSession(null);
            router.navigate(['/home']);
            return throwError(() => err);
          }
        }),
        catchError((refreshErr) => {
          refreshInProgress = false;
          refreshTokenSubject.next(null);
          authState.setSession(null);
          router.navigate(['/home']);
          return throwError(() => refreshErr);
        }),
      );
    }),
  );
};
